import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  FiBox,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiDollarSign,
  FiEdit2,
  FiEye,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiTrash2,
  FiTruck,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, CustomPopup, Input, PageHeader } from '../components/common/index.js';
import { formatPrice } from '../components/stock/InvestmentRecovery.jsx';
import {
  clearStockError,
  createStock,
  deleteStock,
  fetchCarCatalog,
  fetchStocks,
  updateStock,
} from '../redux/slices/stockSlice';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const apiDateFromInput = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

const inputDateFromApi = (value = '') => {
  const match = /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/.exec(value.trim());
  if (match) {
    const month = MONTHS.findIndex((item) => item.toLowerCase() === match[2].toLowerCase());
    if (month >= 0) return `${match[3]}-${String(month + 1).padStart(2, '0')}-${String(match[1]).padStart(2, '0')}`;
  }
  const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return isoMatch?.[1] || '';
};

const errorMessage = (error) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.join(', ');
  if (error.detail || error.message) return error.detail || error.message;
  return Object.entries(error)
    .map(([field, value]) => `${field.replace(/_/g, ' ')}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(' · ');
};

const Modal = ({ title, onClose, children, width = 'max-w-md' }) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 cursor-default bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close dialog" />
      <div className={`relative max-h-[90vh] w-full ${width} overflow-y-auto rounded-2xl bg-white shadow-2xl animate-fade-in-up`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const ComboBox = ({ label, name, value, onChange, error, disabled, required, placeholder, options = [] }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = () => { if (!disabled) { setOpen((v) => !v); setQuery(''); } };
  const select = (opt) => { onChange({ target: { value: opt } }); setOpen(false); setQuery(''); };
  const clear = (e) => { e.stopPropagation(); onChange({ target: { value: '' } }); };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          {label}{required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <div
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={`${name}-listbox`}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
            if (e.key === 'Escape') { setOpen(false); setQuery(''); }
          }}
          className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-sm select-none transition-all duration-200 focus:outline-none focus:ring-2 ${
            error
              ? 'border-rose-400 bg-rose-50 focus:ring-rose-300 focus:border-rose-400'
              : open
              ? 'border-violet-400 ring-2 ring-violet-300/50 bg-white'
              : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400 hover:border-violet-300'
          } ${disabled ? 'bg-slate-50 cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
          <span className={`truncate ${value ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
            {value || placeholder || 'Select…'}
          </span>
          <div className="flex items-center gap-0.5 shrink-0">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={clear}
                className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <FiX className="h-3 w-3" />
              </span>
            )}
            <FiChevronDown className={`h-4 w-4 transition-all duration-200 ${open ? 'rotate-180 text-violet-500' : 'text-slate-400'}`} />
          </div>
        </div>

        {open && (
          <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 animate-fade-in-up">
            {options.length > 5 && (
              <div className="p-2 border-b border-slate-100">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Search…"
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all"
                  />
                </div>
              </div>
            )}
            <ul id={`${name}-listbox`} role="listbox" className="max-h-56 overflow-y-auto py-1.5">
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-sm text-slate-400 text-center">
                  No results for "<span className="font-medium text-slate-500">{query}</span>"
                </li>
              ) : (
                filtered.map((opt) => {
                  const isSelected = value === opt;
                  return (
                    <li key={opt} role="option" aria-selected={isSelected}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); select(opt); }}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                          isSelected
                            ? 'bg-violet-600 text-white font-semibold'
                            : 'text-slate-700 hover:bg-violet-50 hover:text-violet-700'
                        }`}
                      >
                        <span className="truncate">{opt}</span>
                        {isSelected && <FiCheck className="h-3.5 w-3.5 shrink-0" />}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="text-rose-500 text-xs mt-1.5 font-medium">{error}</p>}
    </div>
  );
};

// One search box over the WHOLE catalog (brand + model together) — same
// mechanism as the sales inquiry form. Type "swift" or "maruti" and tap a
// result; brand + model fill in one go. No brand-first two-step.
const VehicleSearchPicker = ({ catalog = [], carName, model, onPick, error, disabled, loading }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  // Flat "Brand Model" list + brand-only rows, searched together
  const q = query.trim().toLowerCase();
  const results = [];
  if (q.length >= 1) {
    for (const brand of catalog) {
      const brandMatch = brand.name.toLowerCase().includes(q);
      if (brandMatch) {
        results.push({ type: 'brand', brand: brand.name, count: brand.models?.length ?? 0 });
      }
      for (const m of brand.models || []) {
        if (results.length >= 30) break;
        const full = `${brand.name} ${m.name}`.toLowerCase();
        if (brandMatch || m.name.toLowerCase().includes(q) || full.includes(q)) {
          results.push({ type: 'model', brand: brand.name, model: m.name });
        }
      }
      if (results.length >= 30) break;
    }
  }

  const pick = (r) => {
    onPick({ car_name: r.brand, model: r.type === 'model' ? r.model : '' });
    setQuery('');
    setOpen(false);
  };

  const pickTyped = () => {
    onPick({ car_name: query.trim(), model: '' });
    setQuery('');
    setOpen(false);
  };

  // Vehicle already chosen → compact card with a change button
  if (carName) {
    return (
      <div className="w-full">
        <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicle<span className="text-rose-500 ml-1">*</span></label>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900">{carName}{model ? ` · ${model}` : ''}</p>
            <p className="text-[11px] text-slate-400">{model ? 'Brand · Model' : 'Brand only — pick a model below if needed'}</p>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onPick({ car_name: '', model: '' })}
            className="shrink-0 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 transition-all"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" ref={containerRef}>
      <label htmlFor="vehicle-search" className="block text-sm font-semibold text-slate-700 mb-2">
        Vehicle<span className="text-rose-500 ml-1">*</span>
      </label>
      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          id="vehicle-search"
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') {
              e.preventDefault();
              if (results.length > 0) pick(results.find((r) => r.type === 'model') || results[0]);
              else if (q.length >= 2) pickTyped();
            }
          }}
          disabled={disabled}
          placeholder={loading ? 'Loading catalogue…' : 'Search brand or model — Swift, Maruti, Polo…'}
          autoComplete="off"
          className={`w-full pl-10 pr-9 py-3 rounded-xl border text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 ${
            error
              ? 'border-rose-400 bg-rose-50 focus:ring-rose-300 focus:border-rose-400'
              : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
          } ${disabled ? 'bg-slate-50 cursor-not-allowed opacity-60' : ''}`}
        />
        {query && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => { setQuery(''); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <FiX className="h-3 w-3" />
          </button>
        )}

        {open && q.length >= 1 && (
          <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 animate-fade-in-up">
            <ul className="max-h-64 overflow-y-auto py-1.5">
              {results.map((r, i) => (
                <li key={`${r.type}-${r.brand}-${r.model || ''}-${i}`}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-violet-50"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[10px] font-bold text-slate-500">
                      {r.brand.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">
                        {r.type === 'model' ? r.model : r.brand}
                      </span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {r.type === 'model' ? r.brand : `Brand · ${r.count} model${r.count === 1 ? '' : 's'}`}
                      </span>
                    </span>
                    <FiCheck className="h-4 w-4 shrink-0 text-violet-400" />
                  </button>
                </li>
              ))}
              {results.length === 0 && (
                <li className="px-4 py-4 text-center">
                  <p className="text-xs font-medium text-slate-500">No matching vehicle in the catalogue</p>
                  {q.length >= 2 && (
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickTyped(); }}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-700 transition-colors"
                    >
                      Use “{query.trim()}” as a new car name
                    </button>
                  )}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">Type brand or model — one tap fills both.</p>
      {error && <p className="text-rose-500 text-xs mt-1 font-medium">{error}</p>}
    </div>
  );
};

const TextareaField = ({ label, name, value, onChange, disabled, placeholder, rows = 3 }) => (
  <div className="w-full">
    {label && <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>}
    <textarea
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-4 py-3 rounded-xl border text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400 resize-none ${disabled ? 'bg-slate-50 cursor-not-allowed opacity-60' : ''}`}
    />
  </div>
);

const VARIANT_OPTIONS = [
  { value: 'pmt', label: 'PMT — Petrol Manual' },
  { value: 'pat', label: 'PAT — Petrol Automatic' },
  { value: 'dmt', label: 'DMT — Diesel Manual' },
  { value: 'dat', label: 'DAT — Diesel Automatic' },
  { value: 'ev', label: 'EV — Electric' },
];

const EMPTY_FORM = {
  purchase_date: '',
  car_name: '',
  model: '',
  year: '',
  description: '',
  rc_status: 'with_rc',
  variant: 'pmt',
  purchase_price: '',
  notes: '',
};

// 4-digit year of manufacture, or a range like 2018-2021 (matches backend)
const YEAR_PATTERN = /^(19|20)\d{2}(\s*-\s*(19|20)\d{2})?$/;

const StockForm = ({ initial = EMPTY_FORM, isEdit = false, submitting, apiError, onSubmit, onClose, catalog = [], catalogLoading = false }) => {
  const [form, setForm] = useState({
    purchase_date: inputDateFromApi(initial.purchase_date),
    car_name: initial.car_name || '',
    model: initial.model || '',
    year: initial.year || '',
    description: initial.description || '',
    rc_status: initial.rc_status || 'with_rc',
    variant: initial.variant || 'pmt',
    purchase_price: initial.purchase_price || '',
    notes: initial.notes || '',
  });
  const [errors, setErrors] = useState({});

  const setField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: '' }));
  };

  // One-tap pick from the search — sets brand + model together
  const handleVehiclePick = ({ car_name, model }) => {
    setForm((current) => ({ ...current, car_name, model }));
    if (errors.car_name) setErrors((current) => ({ ...current, car_name: '' }));
  };

  const selectedEntry = catalog.find((item) => item.name === form.car_name);
  const availableModels = selectedEntry?.models?.map((m) => m.name) ?? [];

  const validate = () => {
    const nextErrors = {};
    if (!form.purchase_date) nextErrors.purchase_date = 'Purchase date is required';
    if (!form.car_name) nextErrors.car_name = 'Car name is required';
    if (!form.purchase_price) {
      nextErrors.purchase_price = 'Purchase price is required';
    } else if (Number(form.purchase_price) <= 0) {
      nextErrors.purchase_price = 'Enter a price greater than zero';
    }
    if (form.year.trim() && !YEAR_PATTERN.test(form.year.trim())) {
      nextErrors.year = 'Enter a year like 2020 (or 2018-2021)';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    onSubmit({
      purchase_date: apiDateFromInput(form.purchase_date),
      car_name: form.car_name,
      model: form.model,
      year: form.year.trim(),
      description: form.description,
      rc_status: form.rc_status,
      variant: form.variant,
      purchase_price: String(form.purchase_price),
      notes: form.notes,
    });
  };

  const formattedError = errorMessage(apiError);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {formattedError && <Alert type="error" message={formattedError} />}

      <Input label="Purchase date" name="purchase_date" type="date" value={form.purchase_date} onChange={setField('purchase_date')} error={errors.purchase_date} required disabled={submitting} />

      <VehicleSearchPicker
        catalog={catalog}
        carName={form.car_name}
        model={form.model}
        onPick={handleVehiclePick}
        error={errors.car_name}
        disabled={submitting}
        loading={catalogLoading}
      />

      {/* Inline model switcher — only for catalogue brands (typed-in cars have no model list) */}
      {form.car_name && availableModels.length > 0 && (
        <ComboBox label="Model" name="model" value={form.model} onChange={setField('model')} disabled={submitting} placeholder="Select model (optional)" options={availableModels} />
      )}

      <Input label="Year (manufacture)" name="year" type="text" inputMode="numeric" placeholder="e.g. 2020" value={form.year} onChange={setField('year')} error={errors.year} disabled={submitting} />

      <Input label="Description" name="description" type="text" placeholder="e.g. Red colour, minor dents on bonnet" value={form.description} onChange={setField('description')} disabled={submitting} />

      <div className="grid grid-cols-2 gap-4">
        <CustomPopup
          label="RC status"
          name="rc_status"
          value={form.rc_status}
          onChange={setField('rc_status')}
          disabled={submitting}
          options={[
            { value: 'with_rc', label: 'With RC' },
            { value: 'without_rc', label: 'Without RC' },
          ]}
        />

        <CustomPopup
          label="Variant"
          name="variant"
          value={form.variant}
          onChange={setField('variant')}
          disabled={submitting}
          options={VARIANT_OPTIONS}
        />
      </div>

      <Input label="Purchase price" name="purchase_price" type="number" inputMode="numeric" min="0" step="any" placeholder="e.g. 180000" value={form.purchase_price} onChange={setField('purchase_price')} error={errors.purchase_price} required disabled={submitting} />

      <TextareaField label="Notes" name="notes" value={form.notes} onChange={setField('notes')} placeholder="Any extra notes…" disabled={submitting} />

      <div className="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-xs leading-relaxed text-violet-700">
        The date will be saved as <strong>{form.purchase_date ? apiDateFromInput(form.purchase_date) : 'DD Mon YYYY'}</strong>.
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="primary" loading={submitting}>{isEdit ? 'Save Changes' : 'Add Stock'}</Button>
      </div>
    </form>
  );
};

const DeleteConfirm = ({ stock, submitting, apiError, onConfirm, onClose }) => {
  const [password, setPassword] = useState('');
  return (
    <Modal title="Delete stock" onClose={onClose}>
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100">
          <FiTrash2 className="h-6 w-6 text-rose-600" />
        </div>
        <p className="font-semibold text-slate-900">Delete {stock.car_name}?</p>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">This will permanently remove this stock record. This action cannot be undone.</p>
        <div className="mt-4 text-left">
          <Input label="Admin password" name="password" type="password" placeholder="Enter your password to confirm" value={password} onChange={(e) => setPassword(e.target.value)} disabled={submitting} required />
        </div>
        {errorMessage(apiError) && <Alert type="error" message={errorMessage(apiError)} className="mt-4 text-left" />}
        <div className="mt-6 flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={submitting} className="flex-1">Cancel</Button>
          <Button variant="danger" onClick={() => onConfirm(password)} loading={submitting} disabled={!password} className="flex-1">Delete</Button>
        </div>
      </div>
    </Modal>
  );
};

// Compact recovery indicator for the list — a mini progress bar + % badge.
// Green once fully recovered (progress_pct >= 100 = broken even / in profit).
const RecoveryBadge = ({ pct }) => {
  const value = Math.max(0, Math.min(Number(pct) || 0, 100));
  const done = value >= 100;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${done ? 'bg-emerald-500' : 'bg-violet-500'}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className={`text-xs font-bold ${done ? 'text-emerald-600' : 'text-slate-500'}`}>
        {value.toFixed(0)}%
      </span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${color.background}`}>
      <Icon className={`h-5 w-5 ${color.icon}`} />
    </div>
    <div className="min-w-0">
      <p className="truncate text-xs text-slate-400">{label}</p>
      <p className="truncate text-lg font-bold text-slate-900">{value}</p>
    </div>
  </div>
);

const StockCard = ({ stock, onView, onEdit, onDelete }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
    <button onClick={onView} className="w-full text-left">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50">
          <FiTruck className="h-5 w-5 text-violet-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-slate-900">{stock.car_name}</p>
          <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{[stock.model, stock.year].filter(Boolean).join(' · ')}</p>
        </div>
        <FiChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
        <div>
          <p className="text-[11px] text-slate-400">Purchase date</p>
          <p className="mt-0.5 text-xs font-semibold text-slate-700">{stock.purchase_date_display || stock.purchase_date || '—'}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-slate-400">Purchase price</p>
          <p className="mt-0.5 text-sm font-bold text-emerald-600">₹{formatPrice(stock.purchase_price)}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
        <div>
          <p className="text-[11px] text-slate-400">Revenue</p>
          <p className={`mt-0.5 text-sm font-bold ${Number(stock.progress_pct) >= 100 ? 'text-emerald-600' : 'text-slate-800'}`}>₹{formatPrice(stock.recovered)}</p>
        </div>
        <RecoveryBadge pct={stock.progress_pct} />
      </div>
    </button>
    <div className="mt-3 flex justify-end gap-1 border-t border-slate-100 pt-3">
      <button onClick={onEdit} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-violet-50 hover:text-violet-600"><FiEdit2 className="h-3.5 w-3.5" /> Edit</button>
      <button onClick={onDelete} className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600"><FiTrash2 className="h-3.5 w-3.5" /> Delete</button>
    </div>
  </div>
);

const ProductsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, count, loading, submitting, error, catalog, catalogLoading } = useSelector((state) => state.stocks);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchStocks());
    dispatch(fetchCarCatalog());
  }, [dispatch]);

  const closeAdd = useCallback(() => {
    setShowAdd(false);
    dispatch(clearStockError());
  }, [dispatch]);

  const closeEdit = useCallback(() => {
    setEditTarget(null);
    dispatch(clearStockError());
  }, [dispatch]);

  const closeDelete = useCallback(() => {
    setDeleteTarget(null);
    dispatch(clearStockError());
  }, [dispatch]);

  const handleCreate = async (data) => {
    const result = await dispatch(createStock(data));
    if (!result.error) {
      closeAdd();
      dispatch(fetchStocks());
    }
  };

  const handleUpdate = async (data) => {
    const result = await dispatch(updateStock({ id: editTarget.id, data }));
    if (!result.error) {
      closeEdit();
      dispatch(fetchStocks());
    }
  };

  const handleDelete = async (password) => {
    const result = await dispatch(deleteStock({ id: deleteTarget.id, password }));
    if (!result.error) closeDelete();
  };

  const handleView = (stock) => {
    navigate(`/products/${stock.id}`);
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return list;
    return list.filter((stock) => [stock.id, stock.car_name, stock.model, stock.year, stock.purchase_date_display, stock.purchase_date, stock.purchase_price]
      .filter((value) => value !== null && value !== undefined)
      .join(' ')
      .toLowerCase()
      .includes(query));
  }, [list, search]);

  const totalInvestment = useMemo(
    () => list.reduce((sum, stock) => sum + (Number(stock.purchase_price) || 0), 0),
    [list]
  );
  const averagePrice = list.length ? totalInvestment / list.length : 0;
  const uniqueCars = useMemo(
    () => new Set(list.map((stock) => stock.car_name?.trim().toLowerCase()).filter(Boolean)).size,
    [list]
  );

  return (
    <MainLayout>
      <div className="space-y-5 sm:space-y-6">
        <PageHeader meta={loading && !list.length ? 'Loading…' : `${count} vehicles in stock`}>
          <button onClick={() => setShowAdd(true)} className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-500/20 transition-all hover:from-violet-700 hover:to-indigo-700 active:scale-95 sm:px-4">
            <FiPlus className="h-4 w-4" /><span className="hidden sm:inline">Add Stock</span><span className="sm:hidden">Add</span>
          </button>
        </PageHeader>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard icon={FiBox} label="Total stock" value={count} color={{ background: 'bg-violet-50', icon: 'text-violet-600' }} />
          <StatCard icon={FiDollarSign} label="Total investment" value={`₹${formatPrice(totalInvestment)}`} color={{ background: 'bg-emerald-50', icon: 'text-emerald-600' }} />
          <StatCard icon={FiTag} label="Average purchase" value={`₹${formatPrice(averagePrice)}`} color={{ background: 'bg-sky-50', icon: 'text-sky-600' }} />
          <StatCard icon={FiTruck} label="Unique cars" value={uniqueCars} color={{ background: 'bg-amber-50', icon: 'text-amber-600' }} />
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <FiSearch className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by car, model, date or price…" className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-10 text-sm text-slate-700 shadow-sm transition-all placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear search"><FiX className="h-4 w-4" /></button>}
          </div>
          <button onClick={() => dispatch(fetchStocks())} disabled={loading} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50" aria-label="Refresh stock">
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && !showAdd && !editTarget && !deleteTarget && <Alert type="error" message={errorMessage(error)} />}

        <div className="lg:hidden">
          {loading && !list.length ? (
            <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" /></div>
          ) : !filtered.length ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center shadow-sm">
              <FiBox className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="font-semibold text-slate-700">{search ? 'No matching stock' : 'No stock added yet'}</p>
              <p className="mt-1 text-sm text-slate-400">{search ? 'Try another search term.' : 'Add your first vehicle stock to get started.'}</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map((stock) => <StockCard key={stock.id} stock={stock} onView={() => handleView(stock)} onEdit={() => setEditTarget(stock)} onDelete={() => setDeleteTarget(stock)} />)}
            </div>
          )}
        </div>

        <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:block">
          {loading && !list.length ? (
            <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" /></div>
          ) : !filtered.length ? (
            <div className="py-16 text-center">
              <FiBox className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="font-semibold text-slate-700">{search ? 'No matching stock' : 'No stock added yet'}</p>
              <p className="mt-1 text-sm text-slate-400">{search ? 'Try another search term.' : 'Add your first vehicle stock to get started.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    {['#', 'Car name', 'Model / description', 'Purchase date', 'Purchase price', 'Revenue', 'Recovery %', 'Actions'].map((heading) => (
                      <th key={heading} className={`px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${heading === 'Actions' ? 'text-right' : 'text-left'}`}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((stock, index) => (
                    <tr key={stock.id} onClick={() => handleView(stock)} className="cursor-pointer transition-colors hover:bg-slate-50/70">
                      <td className="px-6 py-4 text-sm font-semibold text-slate-300">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50"><FiTruck className="h-4 w-4 text-violet-600" /></div>
                          <div><p className="text-sm font-semibold text-slate-800">{stock.car_name}</p><p className="text-xs text-slate-400">Stock #{stock.id}</p></div>
                        </div>
                      </td>
                      <td className="max-w-[280px] px-6 py-4 text-sm text-slate-600"><p className="truncate">{[stock.model, stock.year].filter(Boolean).join(' · ') || '—'}</p></td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-600">{stock.purchase_date_display || stock.purchase_date || '—'}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-emerald-600">₹{formatPrice(stock.purchase_price)}</td>
                      <td className={`whitespace-nowrap px-6 py-4 text-sm font-bold ${Number(stock.progress_pct) >= 100 ? 'text-emerald-600' : 'text-slate-800'}`}>₹{formatPrice(stock.recovered)}</td>
                      <td className="whitespace-nowrap px-6 py-4"><RecoveryBadge pct={stock.progress_pct} /></td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-1">
                          <button onClick={(event) => { event.stopPropagation(); handleView(stock); }} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-sky-50 hover:text-sky-600" title="View"><FiEye className="h-3.5 w-3.5" /></button>
                          <button onClick={(event) => { event.stopPropagation(); setEditTarget(stock); }} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-violet-50 hover:text-violet-600" title="Edit"><FiEdit2 className="h-3.5 w-3.5" /></button>
                          <button onClick={(event) => { event.stopPropagation(); setDeleteTarget(stock); }} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Delete"><FiTrash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAdd && <Modal title="Add vehicle stock" onClose={closeAdd}><StockForm submitting={submitting} apiError={error} onSubmit={handleCreate} onClose={closeAdd} catalog={catalog} catalogLoading={catalogLoading} /></Modal>}
      {editTarget && <Modal title={`Edit ${editTarget.car_name}`} onClose={closeEdit}><StockForm initial={editTarget} isEdit submitting={submitting} apiError={error} onSubmit={handleUpdate} onClose={closeEdit} catalog={catalog} catalogLoading={catalogLoading} /></Modal>}
      {deleteTarget && <DeleteConfirm stock={deleteTarget} submitting={submitting} apiError={error} onConfirm={handleDelete} onClose={closeDelete} />}
    </MainLayout>
  );
};

export default ProductsPage;
